import React from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import "./Node.css";

const GET_NODES = gql`
  query GetNodes { nodes { id name ip status lastSeen createdAt } }
`;
const GET_MY_SITE = gql`
  query GetMySite { mySite { id name installToken } }
`;
const DELETE_NODE = gql`
  mutation DeleteNode($id: ID!) { deleteNode(id: $id) }
`;

export default function NodePage() {
  const { data, loading, refetch } = useQuery(GET_NODES, { pollInterval: 10000 });
  const { data: siteData } = useQuery(GET_MY_SITE);
  const [deleteNode] = useMutation(DELETE_NODE, { onCompleted: () => refetch() });

  const site = siteData?.mySite;
  const token = site?.installToken || "";
  const winUrl   = `/install-node.bat?token=${encodeURIComponent(token)}`;
  const linuxUrl = `/install-node.sh?token=${encodeURIComponent(token)}`;

  const formatDate = (d: string | null) => {
    if (!d) return "—";
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return "just now";
    if (diff < 3600000) return Math.floor(diff / 60000) + " min ago";
    if (diff < 86400000) return Math.floor(diff / 3600000) + " h ago";
    return new Date(d).toLocaleDateString("en-US");
  };

  const isOnline = (lastSeen: string | null) =>
    lastSeen ? Date.now() - new Date(lastSeen).getTime() < 60000 : false;

  const onlineCount = data?.nodes?.filter((n: any) => isOnline(n.lastSeen)).length ?? 0;
  const totalCount = data?.nodes?.length ?? 0;

  return (
    <div className="page-container">
      <div className="page-header"><h2>⬡ Nodes</h2></div>
      <div className="node-page-grid">

        <div className="node-card node-card-wide">
          <div className="node-card-title">Install Connector{site ? ` — ${site.name}` : ""}</div>
          <p className="node-install-desc">
            Download and run the lightweight connector on the facility computer. It registers
            this machine in your site and keeps it online. No Docker required — just download and run.
          </p>
          {!token && (
            <div className="node-control-hint">
              Your account is not bound to a specific site, so a pre-configured installer is unavailable here.
            </div>
          )}
          <div className="install-btns">
            <a className="install-btn windows" href={winUrl} download="install-uav-node.bat">
              <span className="install-btn-icon">🪟</span>
              <div>
                <div className="install-btn-title">Download for Windows</div>
                <div className="install-btn-sub">Run as Administrator (.bat)</div>
              </div>
            </a>
            <a className="install-btn linux" href={linuxUrl} download="install-uav-node.sh">
              <span className="install-btn-icon">🐧</span>
              <div>
                <div className="install-btn-title">Download for Linux</div>
                <div className="install-btn-sub">Ubuntu / Debian / CentOS</div>
              </div>
            </a>
          </div>
          <div className="install-steps">
            <div className="install-step"><span className="step-num">1</span><span>Download the installer for your OS</span></div>
            <div className="install-step"><span className="step-num">2</span><span>Run it (Windows: as Administrator) — the connector installs and auto-starts</span></div>
            <div className="install-step"><span className="step-num">3</span><span>The node appears in the list below within ~30 seconds</span></div>
          </div>
        </div>

        <div className="node-card">
          <div className="node-card-title">Node Network</div>
          <div className="node-network-stats">
            <div className="net-stat">
              <div className="net-stat-val">{totalCount}</div>
              <div className="net-stat-label">Total Nodes</div>
            </div>
            <div className="net-stat">
              <div className="net-stat-val net-online">{onlineCount}</div>
              <div className="net-stat-label">Online</div>
            </div>
            <div className="net-stat">
              <div className="net-stat-val net-offline">{totalCount - onlineCount}</div>
              <div className="net-stat-label">Offline</div>
            </div>
          </div>
        </div>

        <div className="node-card node-card-wide">
          <div className="node-card-header">
            <div className="node-card-title">Active Nodes</div>
            <button className="refresh-btn" onClick={() => refetch()}>↻ Refresh</button>
          </div>
          {loading && <div className="nodes-empty">Loading...</div>}
          {!loading && (!data?.nodes || data.nodes.length === 0) && (
            <div className="nodes-empty">No connected nodes. Install the first node using the buttons above.</div>
          )}
          {data?.nodes && data.nodes.length > 0 && (
            <table className="nodes-table">
              <thead>
                <tr>
                  <th>Status</th><th>Name</th><th>IP Address</th>
                  <th>Last Seen</th><th>Added</th><th></th>
                </tr>
              </thead>
              <tbody>
                {data.nodes.map((node: any) => (
                  <tr key={node.id}>
                    <td>
                      <span className={`node-status-dot ${isOnline(node.lastSeen) ? "dot-online" : "dot-offline"}`} />
                      <span className={isOnline(node.lastSeen) ? "status-online" : "status-offline"}>
                        {isOnline(node.lastSeen) ? "Online" : "Offline"}
                      </span>
                    </td>
                    <td className="node-name">{node.name}</td>
                    <td className="node-ip">{node.ip || "—"}</td>
                    <td>{formatDate(node.lastSeen)}</td>
                    <td>{formatDate(node.createdAt)}</td>
                    <td>
                      <button className="delete-node-btn" title="Delete"
                        onClick={() => { if (window.confirm(`Delete node "${node.name}"?`)) deleteNode({ variables: { id: node.id } }); }}>
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}
